import { useModal } from "@ebay/nice-modal-react";
import { useCallback, useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import FormFieldCronExpressions from "components/FormFields/CronExpressionsField";
import FormFieldTimezone from "components/FormFields/Timezone";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import Feedback from "ds/components/Feedback";
import FormToggleField from "ds/components/Form/ToggleField";
import { DriftDetectionIntegration } from "types/generated";
import { getDrawerFormFix } from "utils/css";
import { VENDOR_DICTIONARY } from "constants/vendor";
import { StackConfigVendorCloudFormation, StackConfigVendor } from "types/generated";
import { getVendorName } from "utils/vendor";
import Card from "ds/components/Card";

import { getFormDefaultValues, mapCreateFormData } from "./helpers";
import { StackSchedulingDriftDetectionFormFields } from "./types";
import useCreateDriftDetection from "./useCreateDriftDetection";
import useUpdateDriftDetection from "./useUpdateDriftDetection";

const isCloudFormationStack = (
  vendorConfig?: StackConfigVendor | null
): vendorConfig is StackConfigVendorCloudFormation => {
  return getVendorName(vendorConfig?.__typename) === VENDOR_DICTIONARY.CLOUD_FORMATION;
};

type StackManageDriftDetectionProps = {
  integration?: DriftDetectionIntegration;
  stackId: string;
  setIsDirty: (isDirty: boolean) => void;
  vendorConfig?: StackConfigVendor | null;
};

const StackManageDriftDetection = ({
  integration,
  stackId,
  setIsDirty,
  vendorConfig,
}: StackManageDriftDetectionProps) => {
  const isEditMode = !!integration;
  const drawer = useModal();

  const { createDriftDetection, loading: creationLoading } = useCreateDriftDetection({
    stackId: stackId,
  });
  const { updateDriftDetection, loading: updateLoading } = useUpdateDriftDetection({
    stackId: stackId,
  });

  const driftDetectionForm = useForm<StackSchedulingDriftDetectionFormFields>({
    defaultValues: getFormDefaultValues(integration),
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty },
  } = driftDetectionForm;

  const isCloudFormation = isCloudFormationStack(vendorConfig);

  const currentReconcileValue = watch("reconcile");

  // For CloudFormation stacks:
  // - When creating: always disable reconcile toggle
  // - When editing: allow disabling if currently enabled, but don't allow re-enabling once disabled
  const shouldDisableReconcile = (() => {
    if (!isCloudFormation) return false;
    if (!isEditMode) return true; // Always disable for new drift detection
    return !currentReconcileValue; // In edit mode, disable only if currently false
  })();

  const handlePrimaryAction = useCallback(
    (formData: StackSchedulingDriftDetectionFormFields) => {
      if (isEditMode) {
        updateDriftDetection(mapCreateFormData(formData), () => {
          drawer.hide();
        });
      } else {
        createDriftDetection(mapCreateFormData(formData), () => {
          drawer.hide();
        });
      }
    },
    [createDriftDetection, isEditMode, updateDriftDetection, drawer]
  );

  useEffect(() => {
    setIsDirty(isDirty);
  }, [setIsDirty, isDirty]);

  return (
    <form onSubmit={handleSubmit(handlePrimaryAction)} {...getDrawerFormFix()}>
      <DrawerHeader justify="between">
        <DrawerHeaderTitle
          title={isEditMode ? "Edit drift detection schedule" : "Create drift detection schedule"}
        />
        <DrawerCloseIcon />
      </DrawerHeader>
      <DrawerBody gap="x-large">
        <Feedback type="banner" variant="info">
          Drift detection only works on private workers.
        </Feedback>

        <FormProvider {...driftDetectionForm}>
          <Card variant="secondary" contentGap="x-large">
            <FormFieldCronExpressions name="cronSchedule" />
            <FormFieldTimezone name="timezone" />
          </Card>

          <Controller
            name="reconcile"
            control={control}
            render={({ field }) => (
              <FormToggleField
                variant="switch"
                title="Reconcile"
                onChange={field.onChange}
                checked={field.value}
                disabled={shouldDisableReconcile}
                description={
                  isCloudFormation
                    ? "Reconciliation is not supported for CloudFormation stacks."
                    : "Indicates whether a reconciliation run should be scheduled when drift is detected. If the drift detection run has failed, a reconciliation run will be triggered regardless, to ensure drift is not missed."
                }
              />
            )}
          />

          <Controller
            name="ignoreState"
            control={control}
            render={({ field }) => (
              <FormToggleField
                variant="switch"
                title="Ignore state"
                onChange={field.onChange}
                checked={field.value}
                description='Indicates whether drift detection should be performed on stacks which are in any state not just "Finished".'
              />
            )}
          />

          <DrawerFooter>
            <DrawerFooterActions>
              <DrawerCancelButton />
              <Button variant="primary" type="submit" loading={creationLoading || updateLoading}>
                {isEditMode ? "Save" : "Create"}
              </Button>
            </DrawerFooterActions>
          </DrawerFooter>
        </FormProvider>
      </DrawerBody>
    </form>
  );
};

export default StackManageDriftDetection;
